import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  selectAnalyticalVariables,
  selectCriteria,
  selectDerivedEvents,
  selectIndexDate,
  selectSimpleEvents,
  selectSubjectVariables,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { getCohortsPath } from '@verantos-dev/core-constants';
import {
  type CohortBuilderDefinitionLegacy,
  type CohortTranslation,
  SelectStatementType,
} from '@verantos-dev/types-cohorts';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { cohortDetailKeys } from './queryKeys';
import { transformCriteriaToPayload } from '../../transformers/criteria/transformCriteriaLegacyToCriteriaPayload';
import { isCriterionResolved } from '../validation/useCohortValidation';

interface UseCohortTranslationQueryParams {
  projectId: string;
  cohortId: string;
  cohortVersionId: string;
}

/**
 * Gen-3 cohort SQL translation read. POSTs the in-memory Zustand
 * cohort definition to the platform's `/translate` endpoint and
 * returns the rendered `sqlQuery`. Replaces the v0 RTK mutation
 * (`useGenerateSQLTranslationMutation`) for the gen-3 path.
 *
 * Why a `useQuery` for a POST: the translate endpoint uses POST
 * because the body carries the full cohort DSL, but semantically
 * it's a read — same input ⇒ same output, no server-side state
 * change. Modeling it as a query lets us cache by definition
 * signature and reuse the result while the user is on the page.
 *
 * The query key includes a definition signature (a stringified
 * subset of the Zustand definition) so TanStack auto-refetches
 * when the user edits the cohort. For a settled saved cohort the
 * signature is stable and the cache hits.
 */
export const useCohortTranslationQuery = ({
  projectId,
  cohortId,
  cohortVersionId,
}: UseCohortTranslationQueryParams) => {
  const criteria = useCohortDefinitionStore(selectCriteria);
  const simpleEvents = useCohortDefinitionStore(selectSimpleEvents);
  const derivedEvents = useCohortDefinitionStore(selectDerivedEvents);
  const indexDate = useCohortDefinitionStore(selectIndexDate);
  const subjectVariables = useCohortDefinitionStore(selectSubjectVariables);
  const analyticalVariables = useCohortDefinitionStore(selectAnalyticalVariables);
  const studyPeriod = useCohortDefinitionStore((s) => s.studyPeriod);

  const cohortJsonDsl: CohortBuilderDefinitionLegacy = useMemo(
    () => ({
      simpleEvents,
      derivedEvents,
      indexDate: indexDate || undefined,
      subjectVariables,
      analyticalVariables,
      criteria: transformCriteriaToPayload(criteria),
      studyPeriodRestrictions: {
        overallStudyPeriodRestriction: studyPeriod.overallStudyPeriodRestriction,
        baselinePeriodRestriction: undefined,
        followUpPeriodRestriction: undefined,
      },
    }),
    [
      simpleEvents,
      derivedEvents,
      indexDate,
      subjectVariables,
      analyticalVariables,
      criteria,
      studyPeriod,
    ]
  );

  // Definition signature: a stringified hash that changes whenever
  // anything that could affect the SQL changes. Cheap enough — small
  // JSON — and avoids re-firing the translate POST on identity churn
  // alone.
  const definitionSignature = useMemo(() => JSON.stringify(cohortJsonDsl), [cohortJsonDsl]);

  const hasCriteria = (cohortJsonDsl.criteria?.length ?? 0) > 0;
  const resolvedCriteria = criteria.filter(isCriterionResolved);
  const hasUnboundCriteria = resolvedCriteria.length < criteria.length;
  const enabled =
    !!projectId && !!cohortId && !!cohortVersionId && hasCriteria && !hasUnboundCriteria;

  return useQuery<TranslationResult>({
    queryKey: [...cohortDetailKeys.version(cohortVersionId), 'translate', definitionSignature],
    queryFn: async () => {
      const response = await authFetch<ObjectResponse<CohortTranslation>>(
        `${getCohortsPath(projectId)}/${cohortId}/translate`,
        {
          method: 'POST',
          body: JSON.stringify({
            selectStmtType: SelectStatementType.ALL,
            cohortJsonDsl,
          }),
        }
      );
      // Backend (OR-6067 fix C) returns HTTP 200 with hasUnresolvedRefs=true
      // when criteria reference an unresolved library AV — empty sql, no error.
      // Surface both fields so the SQL section can branch on "incomplete"
      // separately from "failed to translate" and so save stays decoupled.
      return {
        sqlQuery: response.data?.sqlQuery ?? '',
        hasUnresolvedRefs: response.data?.hasUnresolvedRefs ?? false,
      };
    },
    enabled,
    // Keep the previous SQL on screen while a refetch is in flight so the
    // section doesn't flicker through the empty placeholder on a small edit —
    // but ONLY while the query is enabled. When the definition drops to no /
    // unbound criteria (e.g. a revert), the query disables and never refetches,
    // so carrying the placeholder would strand the pre-revert SQL on screen
    // until a manual reload (OR-6158). Dropping it there clears the SQL to
    // match the empty definition.
    placeholderData: enabled ? (prev) => prev : undefined,
    refetchOnWindowFocus: false,
  });
};

/**
 * Shape consumed by SQL section + Run button. `sqlQuery` is empty whenever
 * `hasUnresolvedRefs` is true; consumers should branch on the flag rather
 * than on the empty string (an empty sqlQuery with the flag false would be
 * a legit "no criteria yet" state).
 */
export interface TranslationResult {
  sqlQuery: string;
  hasUnresolvedRefs: boolean;
}
